import { useCallback, useMemo, useState } from 'react';

import { useActivityBuilderPresentContext } from '../../present/activity-builder-present.context';
import Bouncer from 'approot/shared/bouncer/bouncer';
import {
  ComponentInteractiveResponseCategorise,
  ComponentInteractiveResponseSequence,
} from 'approot/activity-builder/components/component.constants';
import type { InteractiveResponseAnswerFragment } from '../../present/activity-answers.graphql.generated';
import { ClassroomAttemptNav } from '../classroom-attempt';
import { useGetStudentAnswersPollingFallback } from '../use-get-student-answers';
import { AttemptContent } from '../classroom-attempt.style';
import { Student, StudentProfile } from '../classroom.constants';
import ClassroomAnswerDetailHeader from '../classroom-answer-detail-header';

type Props = {
  student: StudentProfile;
  studentsInClass: Student[];
  component: Omit<
    | ComponentInteractiveResponseSequence
    | ComponentInteractiveResponseCategorise,
    '__typename'
  >;
  onSelect: (id: number) => void;
  renderAttempt: ({
    selectedAnswers,
  }: {
    selectedAnswers?: InteractiveResponseAnswerFragment;
  }) => JSX.Element;
};

const InteractiveResponseAttempts = ({
  student,
  studentsInClass,
  component,
  onSelect,
  renderAttempt,
}: Props) => {
  const [index, setIndex] = useState<number>(0);

  const { userClassId } = useActivityBuilderPresentContext();

  const { data } = useGetStudentAnswersPollingFallback({
    userClassId: Number(userClassId),
    componentId: component.id,
    studentId: student.studentId,
  });

  const answers = useMemo(() => data?.getClassStudentsAnswers || [], [data]);

  const currentAnswer = useMemo(() => {
    return answers?.[index]?.answer as InteractiveResponseAnswerFragment;
  }, [answers, index]);

  const handlePrev = useCallback(() => {
    setIndex(index - 1);
  }, [index]);

  const handleNext = useCallback(() => {
    setIndex(index + 1);
  }, [index]);

  if (!data) {
    return <Bouncer />;
  }

  return (
    <>
      <ClassroomAnswerDetailHeader
        studentId={student.studentId}
        students={studentsInClass}
        onSelect={onSelect}
      />
      <AttemptContent>
        {renderAttempt({
          selectedAnswers: currentAnswer,
          ...component,
        })}
      </AttemptContent>
      {answers.length > 1 && (
        <ClassroomAttemptNav
          index={index}
          total={answers.length}
          onNext={handleNext}
          onPrev={handlePrev}
        />
      )}
    </>
  );
};

export default InteractiveResponseAttempts;
